import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MUILink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import { WorkflowModalLayout } from '@/components/shared/workflows/WorkflowModalLayout';
import { WorkflowCartRow } from '@/components/shared/workflows/WorkflowCartRow';
import { useOrderLineItems, useCompleteOrderReceipt, useHierarchyEntities } from '@/hooks';
import { httpClient } from '@/utils/httpClient';
import { documentService } from '@/services/documents';
import { openBase64PdfInWindow, openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { buildLocationLabel, filterLeafLocationOptions } from '@/utils/locationHierarchy';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { useAdminConfigs } from '@/hooks/queries/admin/useAdminConfigs';
import {
  buildCompleteReceiptItems,
  buildReceiveAllCart,
  buildValidLocationSet,
  resolveReceiveLocationId,
} from './receiveOrderPayload';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'ReceiveOrderModal' });
const RECEIVE_COMPLETE_TIMEOUT_MS = 5 * 60 * 1000;
const RECEIVE_DOCUMENT_TIMEOUT_MS = 5 * 60 * 1000;

export interface ReceiveOrderLineItem {
  id: string;
  name: string;
  nsn?: string;
  lin?: string;
  catalogId?: string | null;
  catalogDefaultLocationId?: string | null;
  catalogStorefrontLocationId?: string | null;
  latestReceiptLocationId?: string | null;
  unitPrice?: number;
  /** The actual price paid (may differ from catalog unit price) */
  actualPrice?: number;
  orderedQuantity: number;
  receivedQuantity: number;
  outstandingQuantity: number;
  isFullyReceived: boolean;
}

export interface ReceiveOrderCartItem {
  lineItemId: string;
  quantity: number;
  locationId?: string | null;
}

export interface ReceiveOrderDetails {
  id: string;
  identifier?: string;
  status?: string;
  orderDate?: string;
  logFormNumber?: number | string;
}

export interface ReceiveOrderModalProps {
  open: boolean;
  onClose: () => void;
  order?: ReceiveOrderDetails | null;
  onComplete?: (items: ReceiveOrderCartItem[]) => Promise<void> | void;
}

interface ReceiveCartEntry {
  quantity: number;
  locationId?: string | null;
}

interface LocationOption {
  id: string;
  name: string;
  breadcrumb?: string | null;
  identifier?: string | null;
  nodeTypeName?: string | null;
  label: string;
}

export const ReceiveOrderModal = ({ open, onClose, order, onComplete }: ReceiveOrderModalProps) => {
  const { data: da3161Configs = [] } = useAdminConfigs('da3161_');
  const sendToDefault = (
    (da3161Configs.find((r) => r.config_key === 'da3161_send_to')?.config_value as string) ?? ''
  ).replace(/^"|"$/g, '');
  const lineItemsQuery = useOrderLineItems(order?.id);
  const completeReceipt = useCompleteOrderReceipt();
  const locationQuery = useHierarchyEntities({
    dataSource: {
      endpoint: '/api/rest/data/v1/location_hierarchy',
      order: 'breadcrumb.asc',
    },
    filters: {
      select: 'id,display_name,entity_identifier,breadcrumb,node_type_name',
    },
  });
  const orderDateLabel = useMemo(() => {
    if (!order?.orderDate) {
      return undefined;
    }
    const date = new Date(order.orderDate);
    return Number.isNaN(date.getTime()) ? undefined : date.toLocaleDateString();
  }, [order?.orderDate]);

  const items = useMemo<ReceiveOrderLineItem[]>(() => {
    if (!lineItemsQuery.data) {
      return [];
    }

    return lineItemsQuery.data.map((item) => {
      const ordered = item.entity_data.quantity ?? 0;
      const received =
        item.receipts?.reduce(
          (sum, receipt) => sum + (receipt.entity_data?.quantity_received ?? 0),
          0,
        ) ?? 0;
      const latestReceiptLocationId =
        item.receipts?.find((receipt) => {
          const locationId = receipt.entity_data?.location_id;
          return typeof locationId === 'string' && locationId.length > 0;
        })?.entity_data?.location_id ?? null;
      const outstanding = Math.max(0, ordered - received);

      return {
        id: item.id,
        name:
          item.catalog?.entity_data?.nomenclature ??
          item.catalog?.display_name ??
          item.display_name ??
          'Unknown Item',
        nsn: item.catalog?.entity_data?.nsn,
        lin: item.catalog?.entity_data?.lin,
        catalogId: item.entity_data.catalog_id ?? null,
        catalogDefaultLocationId: item.catalog?.entity_data?.secondary_location_id ?? null,
        catalogStorefrontLocationId: item.catalog?.entity_data?.primary_location_id ?? null,
        latestReceiptLocationId,
        unitPrice: item.entity_data.unit_price_at_time_of_order,
        actualPrice: item.entity_data.actual_price,
        orderedQuantity: ordered,
        receivedQuantity: received,
        outstandingQuantity: outstanding,
        isFullyReceived: outstanding === 0,
      };
    });
  }, [lineItemsQuery.data]);

  const subtitle = useMemo(() => {
    if (!order) {
      return undefined;
    }
    const parts = [];
    const identifier = order.identifier ?? order.id;
    if (identifier) {
      parts.push(identifier);
    }
    if (order.status) {
      parts.push(order.status);
    }
    if (orderDateLabel) {
      parts.push(orderDateLabel);
    }
    return parts.length > 0 ? parts.join(' • ') : undefined;
  }, [order, orderDateLabel]);

  const [receivedCart, setReceivedCart] = useState<Record<string, ReceiveCartEntry>>({});
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>({});
  const [locationSelections, setLocationSelections] = useState<
    Record<string, LocationOption | null>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPublicationDialog, setShowPublicationDialog] = useState(false);
  const [showLargeReceiveWarning, setShowLargeReceiveWarning] = useState(false);
  const defaultPublication = order?.logFormNumber
    ? String(order.logFormNumber).padStart(6, '0')
    : '';
  const [publicationValue, setPublicationValue] = useState(defaultPublication);
  const reqFromDefault = (
    (da3161Configs.find((r) => r.config_key === 'da3161_req_from')?.config_value as string) ?? ''
  ).replace(/^"|"$/g, '');
  const [sendToValue, setSendToValue] = useState(sendToDefault);
  const [requestFromValue, setRequestFromValue] = useState(reqFromDefault);
  const submitLockRef = useRef(false);
  const locationOptions = useMemo<LocationOption[]>(() => {
    return (locationQuery.data ?? []).map((row) => {
      const label = buildLocationLabel(row);
      return {
        id: row.id,
        name: label,
        breadcrumb: row.breadcrumb ?? null,
        identifier: row.entity_identifier ?? null,
        nodeTypeName: row.node_type_name ?? null,
        label,
      };
    });
  }, [locationQuery.data]);

  const leafLocationOptions = useMemo<LocationOption[]>(
    () => filterLeafLocationOptions(locationOptions),
    [locationOptions],
  );

  const leafLocationIdSet = useMemo(
    () => new Set(leafLocationOptions.map((option) => option.id)),
    [leafLocationOptions],
  );

  const locationOptionMap = useMemo(() => {
    const map = new Map<string, LocationOption>();
    for (const option of locationOptions) {
      map.set(option.id, option);
    }
    return map;
  }, [locationOptions]);
  const isLoadingLocations = locationQuery.isLoading;

  const catalogIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of items) {
      if (item.catalogId) ids.add(item.catalogId);
    }
    return [...ids];
  }, [items]);

  const inventoryLocationsQuery = useQuery({
    queryKey: ['receive-order-inventory-locations', catalogIds],
    queryFn: async () => {
      const idsParam = catalogIds.join(',');
      const rows = await httpClient.get<Array<{ catalog_id: string; location_id: string }>>(
        `/api/rest/data/v1/asset_inventory_summary?catalog_id=in.(${idsParam})&is_true_location=eq.true&select=catalog_id,location_id`,
      );
      const map = new Map<string, Set<string>>();
      for (const row of rows) {
        if (!map.has(row.catalog_id)) map.set(row.catalog_id, new Set());
        map.get(row.catalog_id)!.add(row.location_id);
      }
      return map;
    },
    enabled: open && catalogIds.length > 0,
    staleTime: 30_000,
  });

  const validLocationsByItem = useMemo(() => {
    const result = new Map<string, Set<string> | null>();
    for (const item of items) {
      const inventoryLocs = item.catalogId
        ? inventoryLocationsQuery.data?.get(item.catalogId)
        : undefined;
      result.set(
        item.id,
        buildValidLocationSet({
          catalogDefaultLocationId: item.catalogDefaultLocationId,
          catalogStorefrontLocationId: item.catalogStorefrontLocationId,
          latestReceiptLocationId: item.latestReceiptLocationId,
          inventoryLocationIds: inventoryLocs ? [...inventoryLocs] : [],
        }),
      );
    }
    return result;
  }, [items, inventoryLocationsQuery.data]);

  const itemLocationOptions = useMemo(() => {
    const result = new Map<string, LocationOption[]>();
    for (const item of items) {
      const validSet = validLocationsByItem.get(item.id);
      result.set(
        item.id,
        validSet ? leafLocationOptions.filter((opt) => validSet.has(opt.id)) : leafLocationOptions,
      );
    }
    return result;
  }, [items, validLocationsByItem, leafLocationOptions]);

  const catalogDefaultLocationIds = useMemo(() => {
    const defaults: Record<string, string | null> = {};
    for (const item of items) {
      defaults[item.id] = item.catalogDefaultLocationId ?? null;
    }
    return defaults;
  }, [items]);

  const latestReceiptLocationIds = useMemo(() => {
    const latest: Record<string, string | null> = {};
    for (const item of items) {
      latest[item.id] = item.latestReceiptLocationId ?? null;
    }
    return latest;
  }, [items]);

  const resolveLocationSelection = (lineItemId: string): LocationOption | null => {
    const resolvedLocationId = resolveReceiveLocationId({
      selectedLocationId: locationSelections[lineItemId]?.id ?? null,
      cartLocationId: receivedCart[lineItemId]?.locationId ?? null,
      latestReceiptLocationId: latestReceiptLocationIds[lineItemId] ?? null,
      catalogDefaultLocationId: catalogDefaultLocationIds[lineItemId] ?? null,
      isValidLocationId: (locationId) => leafLocationIdSet.has(locationId),
    });
    if (!resolvedLocationId) {
      return null;
    }
    return locationOptionMap.get(resolvedLocationId) ?? null;
  };

  useEffect(() => {
    if (!open) {
      setReceivedCart({});
      setQuantityDrafts({});
      setLocationSelections({});
      setIsSubmitting(false);
      setErrorMessage(null);
      setShowPublicationDialog(false);
      setPublicationValue(defaultPublication);
      setSendToValue(sendToDefault);
      setRequestFromValue(reqFromDefault);
      return;
    }

    // Pre-populate publication with the log form number when the modal opens
    setPublicationValue(defaultPublication);

    const initialDrafts = Object.fromEntries(
      items.map((item) => [item.id, String(item.outstandingQuantity)]),
    );
    setQuantityDrafts(initialDrafts);
    setLocationSelections((previous) => {
      const next: Record<string, LocationOption | null> = {};
      for (const item of items) {
        // Prefer the latest receipt location, then fall back to catalog default.
        const latestReceiptLocation = item.latestReceiptLocationId
          ? locationOptionMap.get(item.latestReceiptLocationId)
          : null;
        const catalogDefaultLocation = item.catalogDefaultLocationId
          ? locationOptionMap.get(item.catalogDefaultLocationId)
          : null;
        next[item.id] =
          previous[item.id] ?? latestReceiptLocation ?? catalogDefaultLocation ?? null;
      }
      return next;
    });
  }, [open, items, locationOptionMap, defaultPublication, reqFromDefault, sendToDefault]);

  useEffect(() => {
    setLocationSelections((previous) => {
      const next: Record<string, LocationOption | null> = {};
      for (const [lineItemId, selection] of Object.entries(previous)) {
        if (selection) {
          const canonical = locationOptionMap.get(selection.id) ?? selection;
          next[lineItemId] = leafLocationIdSet.has(canonical.id) ? canonical : null;
        } else {
          next[lineItemId] = null;
        }
      }
      return next;
    });
    setReceivedCart((previous) => {
      const entries = Object.entries(previous);
      if (entries.length === 0) {
        return previous;
      }

      let changed = false;
      const next: Record<string, ReceiveCartEntry> = {};
      for (const [lineItemId, entry] of entries) {
        if (entry.locationId && !leafLocationIdSet.has(entry.locationId)) {
          changed = true;
          next[lineItemId] = { ...entry, locationId: null };
        } else {
          next[lineItemId] = entry;
        }
      }

      return changed ? next : previous;
    });
  }, [locationOptionMap, leafLocationIdSet]);

  const handleQuantityChange = (lineItemId: string, value: string) => {
    setQuantityDrafts((previous) => ({
      ...previous,
      [lineItemId]: value,
    }));
  };

  const handleLocationChange = (lineItemId: string, selection: LocationOption | null) => {
    // Store the canonical selection from the option map to ensure reference stability
    const canonicalSelection = selection
      ? (locationOptionMap.get(selection.id) ?? selection)
      : null;

    setLocationSelections((previous) => ({
      ...previous,
      [lineItemId]: canonicalSelection,
    }));

    setReceivedCart((previous) => {
      if (!previous[lineItemId]) {
        return previous;
      }
      const locationId = canonicalSelection?.id ?? null;
      return {
        ...previous,
        [lineItemId]: {
          ...previous[lineItemId],
          locationId,
        },
      };
    });
  };

  const handleAddToCart = (lineItemId: string) => {
    const item = items.find((entry) => entry.id === lineItemId);
    if (!item) {
      return;
    }
    setErrorMessage(null);
    const draftValue = quantityDrafts[lineItemId];
    const parsed = Number.parseInt(draftValue ?? '', 10);
    const clamped =
      Number.isFinite(parsed) && parsed > 0
        ? Math.min(parsed, item.outstandingQuantity)
        : item.outstandingQuantity;

    const selection = resolveLocationSelection(lineItemId);
    if (!selection?.id) {
      setErrorMessage('Select a location before adding items to the receive cart.');
      return;
    }

    setReceivedCart((previous) => ({
      ...previous,
      [lineItemId]: {
        quantity: clamped,
        locationId: selection?.id ?? null,
      },
    }));
    setQuantityDrafts((previous) => ({
      ...previous,
      [lineItemId]: String(clamped),
    }));
  };

  const handleRemoveFromCart = (lineItemId: string) => {
    setReceivedCart((previous) => {
      const { [lineItemId]: _, ...rest } = previous;
      return rest;
    });
  };

  const handleReceiveAll = () => {
    setErrorMessage(null);
    const missingLocationCount = items.filter(
      (item) => item.outstandingQuantity > 0 && !resolveLocationSelection(item.id)?.id,
    ).length;
    if (missingLocationCount > 0) {
      setErrorMessage('Select a location for each line item before receiving all.');
      return;
    }

    const nextCart = buildReceiveAllCart(
      items,
      (lineItemId) => resolveLocationSelection(lineItemId)?.id ?? null,
    );
    setReceivedCart(nextCart);
    // Build quantity drafts from the same receivable items (outstandingQuantity > 0)
    const receivableItems = items.filter((item) => item.outstandingQuantity > 0);
    const nextDrafts = Object.fromEntries(
      receivableItems.map((item) => [item.id, String(item.outstandingQuantity)]),
    );
    setQuantityDrafts(nextDrafts);
  };

  // Show publication dialog before completing
  const handleComplete = () => {
    const cartItems: ReceiveOrderCartItem[] = buildCompleteReceiptItems(receivedCart);

    if (cartItems.length === 0) {
      setErrorMessage('Add at least one asset to the received cart before completing.');
      return;
    }

    if (cartItems.some((item) => !item.locationId)) {
      setErrorMessage('Location is required for every received line item.');
      return;
    }

    if (!order?.id) {
      setErrorMessage('Order ID is missing.');
      return;
    }

    setErrorMessage(null);

    // Warn when the receive will complete the entire order with a large quantity (AB#29102)
    const totalCartQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalOutstanding = items.reduce((sum, item) => sum + item.outstandingQuantity, 0);
    if (totalCartQuantity >= totalOutstanding && totalOutstanding > 100) {
      setShowLargeReceiveWarning(true);
      return;
    }

    setShowPublicationDialog(true);
  };

  const handleConfirmLargeReceive = () => {
    setShowLargeReceiveWarning(false);
    setShowPublicationDialog(true);
  };

  // Actually complete the receipt after publication is entered
  const handleConfirmComplete = async () => {
    if (submitLockRef.current || isSubmitting || completeReceipt.isPending) {
      return;
    }

    submitLockRef.current = true;
    setShowPublicationDialog(false);

    const cartItems: ReceiveOrderCartItem[] = buildCompleteReceiptItems(receivedCart);

    if (!order?.id) {
      setErrorMessage('Order ID is missing.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    // Pre-open window synchronously during user-gesture context (before any await)
    // to prevent browsers from blocking the popup as a non-user-initiated action.
    const docWindow = preOpenWindow();

    try {
      const receiptResult = await completeReceipt.mutateAsync({
        orderId: order.id,
        items: cartItems,
        timeoutMs: RECEIVE_COMPLETE_TIMEOUT_MS,
      });

      // Generate DA 3161 for the receipt
      try {
        const receivedItems = items
          .filter((item) => receivedCart[item.id])
          .map((item, index) => {
            const supplyAction = receivedCart[item.id]?.quantity ?? 0; // Actual quantity received
            const orderedQuantity = item.orderedQuantity; // Quantity from PO/requisition
            // Use actual_price (the price paid) for document, fallback to unit_price if not set
            const actualPrice = item.actualPrice ?? item.unitPrice ?? 0;
            const totalCost = supplyAction * actualPrice; // Total cost = supply action × actual price
            // Build stock_number: LIN on first line, NSN on second line
            const stockNumberParts: string[] = [];
            if (item.lin) stockNumberParts.push(item.lin);
            if (item.nsn) stockNumberParts.push(item.nsn);
            const stockNumber = stockNumberParts.join('\n');
            return {
              line_number: String(index + 1), // Sequential line number (1, 2, 3...)
              description: item.name,
              stock_number: stockNumber, // LIN on line 1, NSN on line 2
              quantity: orderedQuantity, // Quantity column = ordered quantity from PO
              supply_action: supplyAction, // Supply Action column = actual received
              code: 'I', // Code column = I (Initial Issue)
              unit_of_issue: 'EA', // Each - standard unit of issue
              actual_price: actualPrice, // Use actual price (what was paid) for the document
              total_cost: totalCost, // Total Cost = supply_action × actual_price
            };
          });

        // Sort items by LIN before generating document
        receivedItems.sort((a, b) => {
          const linA = a.stock_number.split('\n')[0] ?? '';
          const linB = b.stock_number.split('\n')[0] ?? '';
          return linA.localeCompare(linB);
        });

        // Re-assign sequential line numbers after sorting
        for (let i = 0; i < receivedItems.length; i++) {
          const item = receivedItems[i];
          if (item) item.line_number = String(i + 1);
        }

        // Calculate totals
        const sheetTotal = receivedItems.reduce((sum, item) => sum + item.total_cost, 0);
        const grandTotal = sheetTotal;

        // Use the receipt document number from the API response (e.g., "6033-4001-A")
        // This is the proper document number from the document_number_series
        const documentNumber =
          receiptResult.receiptDocumentNumber ??
          order.identifier ??
          `ORD-${order.id.slice(0, 8).toUpperCase()}`;

        // Format date as YYYYMMDD per DA 3161 standard (no dashes)
        const now = new Date();
        const transactionDate = now.toISOString().split('T')[0]?.replace(/-/g, '') ?? '';

        const docResult = await documentService.generateDocument(
          {
            documentType: 'DA3161',
            payload: {
              // Header fields per DA 3161 requirements
              document_number: documentNumber,
              issue_type: true,
              issue_from: requestFromValue,
              dodaac: 'W912HN', // TODO: Pull from instance configuration
              send_to: sendToValue,
              transaction_date: transactionDate, // Auto-filled as YYYYMMDD
              priority: '', // Blank by default
              accounting_9a: '', // Blank by default
              accounting_9b: '', // Blank by default
              job_order: '', // Blank by default
              publication: publicationValue, // User-entered publication reference
              // Line items
              items: receivedItems,
              sheet_total: sheetTotal,
              grand_total: grandTotal,
            },
            ...(receiptResult.transactionId
              ? { options: { metadata: { transactionId: receiptResult.transactionId } } }
              : {}),
          },
          { timeout: RECEIVE_DOCUMENT_TIMEOUT_MS },
        );

        if (docResult.outputBuffer) {
          const opened = openBase64PdfInWindow(docResult.outputBuffer, docWindow);
          if (!opened && docResult.downloadUrl) {
            // Inline open failed — fall back to authenticated download
            const fallbackUrl = documentService.resolveDownloadUrl(docResult.downloadUrl);
            if (fallbackUrl) await openDocumentWithAuth(fallbackUrl);
          }
        } else if (docResult.downloadUrl) {
          const url = documentService.resolveDownloadUrl(docResult.downloadUrl);
          if (url) {
            await openDocumentWithAuth(url, docWindow);
          } else if (docWindow && !docWindow.closed) {
            docWindow.close();
          }
        } else if (docWindow && !docWindow.closed) {
          docWindow.close();
        }

        // Link the document entity to the transaction so it appears in the transaction log
        if (docResult.documentEntityId && receiptResult.transactionId) {
          try {
            await documentService.linkDocumentToTransaction(
              docResult.documentEntityId,
              receiptResult.transactionId,
            );
            log.debug('Document linked to transaction for transaction log', {
              documentEntityId: docResult.documentEntityId,
              transactionId: receiptResult.transactionId,
              orderIdentifier: order.identifier,
            });
          } catch (linkError) {
            log.warn('Failed to link document to transaction', { error: String(linkError) });
            // Don't block - document was generated successfully
          }
        } else {
          log.debug('Cannot link document to transaction - missing IDs', {
            documentEntityId: docResult.documentEntityId,
            transactionId: receiptResult.transactionId,
          });
        }
      } catch (docError) {
        log.error('Failed to generate DA 3161', { error: String(docError) });
        if (docWindow && !docWindow.closed) docWindow.close();
        // Don't block completion if doc generation fails, just log it
      }

      // Call optional onComplete callback if provided
      if (onComplete) {
        await onComplete(cartItems);
      }

      onClose();
    } catch (error) {
      // Error is already handled by the mutation hook with notifications
      // Just display in the modal as well
      const message = error instanceof Error ? error.message : 'Failed to complete order receipt.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const cartEntries = Object.entries(receivedCart);
  const totalSelected = cartEntries.reduce((sum, [, entry]) => sum + entry.quantity, 0);

  const plannerToolbar = (
    <Stack spacing={1.5}>
      {errorMessage ? (
        <Box sx={{ p: 2, borderRadius: 1, bgcolor: (theme) => theme.palette.error.light }}>
          <Typography variant="body2" color="error.contrastText">
            {errorMessage}
          </Typography>
        </Box>
      ) : null}
      <Typography variant="body2" color="text.secondary">
        Track outstanding quantities and mark received assets.
      </Typography>
    </Stack>
  );

  const plannerContent = (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack sx={{ flex: 1, overflowY: 'auto' }}>
        {items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No line items found for this order.</Typography>
          </Box>
        ) : (
          items.map((item) => {
            const outstanding = item.outstandingQuantity;
            const draftValue = quantityDrafts[item.id] ?? String(outstanding);
            const inCart = receivedCart[item.id] != null;
            const resolvedSelection = resolveLocationSelection(item.id);
            return (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  alignItems: { md: 'center' },
                  px: 3,
                  py: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: item.isFullyReceived
                    ? (theme) => theme.palette.success.light
                    : undefined,
                  opacity: item.isFullyReceived ? 0.6 : 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.name}
                    {item.isFullyReceived && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          ml: 1,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: 'success.main',
                          color: 'success.contrastText',
                        }}
                      >
                        Fully Received
                      </Typography>
                    )}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                    {item.nsn && (
                      <Typography variant="body2" color="text.secondary">
                        NSN: {item.nsn}
                      </Typography>
                    )}
                    {item.lin && (
                      <Typography variant="body2" color="text.secondary">
                        LIN: {item.lin}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Received {item.receivedQuantity} / Outstanding {outstanding} / Ordered{' '}
                    {item.orderedQuantity}
                  </Typography>
                </Box>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  sx={{ width: '100%', maxWidth: 420 }}
                >
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 200 }}>
                    <TextField
                      size="small"
                      label="Quantity Received"
                      type="number"
                      value={draftValue}
                      onChange={(event) => handleQuantityChange(item.id, event.target.value)}
                      disabled={item.isFullyReceived}
                      slotProps={{ htmlInput: { min: 1, max: outstanding } }}
                    />
                    <Autocomplete<LocationOption>
                      size="small"
                      options={itemLocationOptions.get(item.id) ?? leafLocationOptions}
                      value={resolvedSelection}
                      onChange={(_event, newValue) => {
                        handleLocationChange(item.id, newValue);
                      }}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      loading={isLoadingLocations}
                      disabled={item.isFullyReceived}
                      renderOption={(props, option) => {
                        const { key: _key, ...otherProps } = props;
                        return (
                          <li key={option.id} {...otherProps}>
                            <Stack>
                              <Typography variant="body2">{option.name}</Typography>
                              {option.breadcrumb ? (
                                <Typography variant="caption" color="text.secondary">
                                  {option.breadcrumb}
                                </Typography>
                              ) : null}
                            </Stack>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Apply Location"
                          placeholder="Shelf, room, bin..."
                          error={!item.catalogDefaultLocationId && !resolvedSelection}
                          helperText={
                            !item.catalogDefaultLocationId && !resolvedSelection
                              ? 'Location is missing in catalog'
                              : undefined
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingLocations ? (
                                  <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      loadingText="Loading locations…"
                      noOptionsText="No locations found"
                    />
                  </Stack>
                  <Button
                    variant={inCart ? 'outlined' : 'contained'}
                    color={inCart ? 'error' : 'primary'}
                    onClick={() =>
                      inCart ? handleRemoveFromCart(item.id) : handleAddToCart(item.id)
                    }
                    disabled={item.isFullyReceived || !resolvedSelection}
                    sx={{ whiteSpace: 'nowrap', minWidth: 90 }}
                  >
                    {inCart ? 'Remove' : 'Add'}
                  </Button>
                </Stack>
              </Box>
            );
          })
        )}
      </Stack>
    </Paper>
  );

  const sidebarContent = (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2, maxHeight: '40vh', overflowY: 'auto' }}>
        <Stack spacing={1.5}>
          {cartEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No assets added yet
            </Typography>
          ) : (
            cartEntries.map(([lineItemId, entry]) => {
              const item = items.find((entry) => entry.id === lineItemId);
              if (!item) {
                return null;
              }
              const locationOption = entry.locationId
                ? locationOptionMap.get(entry.locationId)
                : undefined;
              const subtitleParts = [
                `Received ${entry.quantity} / Ordered ${item.orderedQuantity}`,
              ];
              if (locationOption) {
                subtitleParts.push(`Location ${locationOption.breadcrumb ?? locationOption.name}`);
              }

              return (
                <WorkflowCartRow
                  key={lineItemId}
                  title={item.name}
                  subtitle={subtitleParts.join(' • ')}
                  actions={
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={`Remove ${item.name} from receive cart`}
                      onClick={() => handleRemoveFromCart(lineItemId)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                />
              );
            })
          )}
        </Stack>
      </Paper>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {totalSelected} assets selected
        </Typography>
      </Box>
    </Stack>
  );

  const footer = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
      <ViewerGuardedButton
        variant="outlined"
        startIcon={<PlaylistAddCheckIcon />}
        onClick={handleReceiveAll}
        disabled={items.length === 0}
      >
        Receive All
      </ViewerGuardedButton>
      <ViewerGuardedButton
        variant="contained"
        color="primary"
        startIcon={<ShoppingCartCheckoutIcon />}
        onClick={handleComplete}
        disabled={isSubmitting || cartEntries.length === 0}
      >
        {isSubmitting ? 'Completing…' : 'Complete'}
      </ViewerGuardedButton>
    </Box>
  );

  return (
    <>
      <WorkflowModalLayout
        open={open}
        onClose={onClose}
        title="Receive Order"
        subTitle={subtitle}
        disableFocusTrap={showPublicationDialog}
        breadcrumbs={
          <Breadcrumbs aria-label="Breadcrumb">
            <MUILink
              underline="hover"
              color="inherit"
              href="#"
              onClick={(event) => event.preventDefault()}
            >
              Supply
            </MUILink>
            <Typography color="text.primary">Order Receive</Typography>
          </Breadcrumbs>
        }
        plannerTitle="Order Line Items"
        plannerToolbar={plannerToolbar}
        plannerContent={plannerContent}
        sidebarTitle="Receive Cart"
        sidebarContent={sidebarContent}
        footer={footer}
      />

      {/* Publication Dialog */}
      <Dialog
        open={showPublicationDialog}
        onClose={() => setShowPublicationDialog(false)}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>Document Details</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Send To"
            fullWidth
            variant="outlined"
            value={sendToValue}
            onChange={(e) => setSendToValue(e.target.value)}
            placeholder="Enter send to..."
            sx={{ mt: 1 }}
            inputProps={{ autoComplete: 'off' }}
          />
          <TextField
            margin="dense"
            label="Request From"
            fullWidth
            variant="outlined"
            value={requestFromValue}
            onChange={(e) => setRequestFromValue(e.target.value)}
            placeholder="Enter request from..."
            sx={{ mt: 1 }}
            inputProps={{ autoComplete: 'off' }}
          />
          <TextField
            margin="dense"
            label="Publication"
            fullWidth
            variant="outlined"
            value={publicationValue}
            onChange={(e) => setPublicationValue(e.target.value)}
            placeholder="Enter publication reference..."
            sx={{ mt: 1 }}
            inputProps={{ autoComplete: 'off' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPublicationDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmComplete} variant="contained" color="primary">
            Complete Receipt
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showLargeReceiveWarning}
        onClose={() => setShowLargeReceiveWarning(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Full Receipt</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            You are about to receive <strong>all remaining items</strong> on this order (
            {items.reduce((sum, item) => sum + item.outstandingQuantity, 0).toLocaleString()}{' '}
            assets). This will mark the order as completed and prevent further receiving.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            If you only intend a partial receipt, cancel and adjust the quantities in the cart
            first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLargeReceiveWarning(false)}>Cancel</Button>
          <Button onClick={handleConfirmLargeReceive} variant="contained" color="warning">
            Yes, Receive All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
